import React, { useState } from 'react';
import { ethers } from 'ethers';
import Button from '../common/Button';
import InlineSpinner from '../loader/InlineSpinner';
import { useWallet } from '../../context/WalletContext';
import { useNotify } from '../../context/NotificationContext';
import { getADNFTContract } from '../../web3/contractInstance';
import { makeReadProvider } from '../../web3/providerHelper';
import { pickAvatar } from '../../utils/userAvatar';
import { formatUSDT, shortAddress, formatDate } from '../../utils/format';
import { ACTIVE } from '../../config/networks';

// Accepts an address or a numeric user ID, resolves to a userId, then fetches everything.
export default function UserLookup() {
  const { getEthersProvider } = useWallet();
  const notify = useNotify();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  async function lookup() {
    const q = (query || '').trim();
    if (!q) { notify.error('Enter an address or user ID'); return; }

    setLoading(true);
    setData(null);
    setNotFound(false);
    try {
      const ethersProvider = getEthersProvider();
      const provider = ethersProvider ? ethersProvider : makeReadProvider();
      const c = getADNFTContract(provider);

      // Resolve userId
      let uid;
      if (ethers.utils.isAddress(q)) {
        const id = await c.addressToId(q);
        uid = id.toNumber ? id.toNumber() : Number(id);
      } else if (/^\d+$/.test(q)) {
        uid = Number(q);
      } else {
        notify.error('Invalid input', 'Enter a wallet address or a numeric user ID');
        setLoading(false);
        return;
      }

      if (!uid || uid === 0) {
        setNotFound(true);
        return;
      }

      // Fan out all reads
      const [basic, extended, income, unlock, isBlacklisted, invCount] = await Promise.all([
        c.getUserBasicInfo(uid).catch(() => null),
        c.getUserExtendedInfo(uid).catch(() => null),
        c.getIncomeBreakdown(uid).catch(() => null),
        c.getLevelUnlockInfo(uid).catch(() => null),
        c.blacklistedUsers(uid).catch(() => false),
        c.getInvestmentCount(uid).catch(() => 0)
      ]);

      const wallet = basic ? (basic.wallet || basic[0]) : null;
      if (!wallet || wallet === ethers.constants.AddressZero) {
        setNotFound(true);
        return;
      }

      const shaped = {
        uid,
        wallet,
        totalInvestment: basic.totalInvestment || basic[1],
        totalEarned:     basic.totalEarned     || basic[2],
        pending:         basic.pendingRewards  || basic[3],
        lastTradeTime:   Number((basic.lastTradeTime || basic[4]).toString()),
        directs:         Number((extended ? (extended.directReferrals || extended[0]) : 0).toString()),
        teamVolume:      extended ? (extended.teamVolume || extended[1]) : 0,
        referrerId:      Number((extended ? (extended.referrerId || extended[2]) : 0).toString()),
        active:          extended ? (extended.active !== undefined ? extended.active : extended[3]) : false,
        isBlacklisted:   !!isBlacklisted,
        invCount:        (invCount.toNumber ? invCount.toNumber() : Number(invCount)) || 0,
        income: income ? {
          roi:     income.roi          || income[0],
          level:   income.levelIncome  || income[1],
          direct:  income.directIncome || income[2],
          earned:  income.totalEarnedOut || income[3],
          pending: income.pending      || income[4],
          claimed: income.claimed      || income[5]
        } : null,
        unlock: unlock ? {
          qualifying: Number((unlock.qualifyingDirects || unlock[0]).toString()),
          natural:    Number((unlock.naturalUnlocked   || unlock[1]).toString()),
          override:   Number((unlock.adminOverride     || unlock[2]).toString()),
          effective:  Number((unlock.effectiveUnlocked || unlock[3]).toString())
        } : null
      };
      setData(shaped);
    } catch (e) {
      notify.error('Lookup failed', e && (e.message || String(e)));
    } finally {
      setLoading(false);
    }
  }

  function copy(v) {
    try {
      navigator.clipboard.writeText(v).then(() => notify.success('Copied'));
    } catch (_e) {
      notify.error('Copy failed');
    }
  }

  function onKey(e) { if (e.key === 'Enter') lookup(); }

  return (
    <div className="admin-section">
      <h3>User Lookup</h3>
      <div className="card admin-card">
        <div className="field" style={{ marginBottom: 8 }}>
          <label>Search by Wallet Address or User ID</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="input"
              placeholder="0x… or e.g. 5000"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKey}
            />
            <Button onClick={lookup} loading={loading}>
              {loading ? '' : 'Search'}
            </Button>
          </div>
          <div className="hint-msg">Press Enter to search.</div>
        </div>

        {loading && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 12, color: 'var(--text-dim)', justifyContent: 'center' }}>
            <InlineSpinner /> Fetching user…
          </div>
        )}

        {notFound && !loading && (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-mute)' }}>
            No user found for that address or ID.
          </div>
        )}

        {data && !loading && (
          <div className="lookup-result">
            {/* Header row */}
            <div className="lookup-head">
              <div className="avatar"><img src={pickAvatar(data.wallet)} alt="" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span className="lookup-id">ID #{data.uid}</span>
                  <span className="badge mute">Upline #{data.referrerId}</span>
                  {data.active
                    ? <span className="badge success">Active</span>
                    : <span className="badge mute">Inactive</span>}
                  {data.isBlacklisted && <span className="badge danger">Blacklisted</span>}
                </div>
                <div className="lookup-wallet" title={data.wallet}>{shortAddress(data.wallet)}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => copy(data.wallet)}>Copy</button>
                  <a className="btn btn-ghost btn-sm" href={`${ACTIVE.explorer}/address/${data.wallet}`} target="_blank" rel="noreferrer">Explorer</a>
                </div>
              </div>
            </div>

            {/* Core stats */}
            <div className="stat-grid" style={{ marginTop: 12 }}>
              <div className="stat"><div className="label">Total Invested</div><div className="value">{formatUSDT(data.totalInvestment)}</div></div>
              <div className="stat"><div className="label">Total Earned</div><div className="value">{formatUSDT(data.totalEarned)}</div></div>
              <div className="stat"><div className="label">Pending</div><div className="value">{formatUSDT(data.pending)}</div></div>
              <div className="stat"><div className="label">Directs</div><div className="value">{data.directs}</div></div>
              <div className="stat"><div className="label">Team Volume</div><div className="value">{formatUSDT(data.teamVolume)}</div></div>
              <div className="stat"><div className="label">Investments</div><div className="value">{data.invCount}</div></div>
            </div>

            {/* Income breakdown */}
            {data.income && (
              <div className="card" style={{ marginTop: 12, padding: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 6, color: 'var(--g-700)' }}>Income Breakdown</div>
                <div className="card-row"><span className="k">Trading Profit</span><span className="v">{formatUSDT(data.income.roi)}</span></div>
                <div className="card-row"><span className="k">Level Income</span><span className="v">{formatUSDT(data.income.level)}</span></div>
                <div className="card-row"><span className="k">Direct Income (legacy)</span><span className="v">{formatUSDT(data.income.direct)}</span></div>
                <div className="card-row"><span className="k">Total Earned</span><span className="v">{formatUSDT(data.income.earned)}</span></div>
                <div className="card-row"><span className="k">Claimed (lifetime)</span><span className="v">{formatUSDT(data.income.claimed)}</span></div>
              </div>
            )}

            {/* Unlock info */}
            {data.unlock && (
              <div className="card" style={{ marginTop: 12, padding: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 6, color: 'var(--g-700)' }}>Level Unlock</div>
                <div className="card-row"><span className="k">Qualifying Directs</span><span className="v">{data.unlock.qualifying}</span></div>
                <div className="card-row"><span className="k">Natural Unlocked</span><span className="v">{data.unlock.natural} / 15</span></div>
                <div className="card-row"><span className="k">Admin Override</span><span className="v">{data.unlock.override || '—'}</span></div>
                <div className="card-row"><span className="k">Effective Unlocked</span><span className="v" style={{ color: 'var(--g-700)' }}>{data.unlock.effective} / 15</span></div>
              </div>
            )}

            {/* Timing */}
            <div className="card" style={{ marginTop: 12, padding: 12 }}>
              <div className="card-row"><span className="k">Last Start Trade</span><span className="v">{data.lastTradeTime ? formatDate(data.lastTradeTime) : '—'}</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
